// Brand glyphs for the five social channels Aranor publishes on.
// lucide-react v1.16.0 dropped all brand marks (Instagram, LinkedIn,
// YouTube etc.) — they live in `simple-icons` upstream now. To avoid
// pulling another icon dependency, every social glyph used in the app
// is hand-rolled here.
//
// All single-color, 24x24 viewBox, `currentColor` fills so Tailwind
// text utilities (e.g. `text-warm-100/80`, `hover:text-brand-primary`)
// control the color just like the rest of our icons. Each component
// accepts the same minimal prop surface (`className`, plus any
// standard SVG attribute) so they can be dropped into the SOCIALS
// list interchangeably.
import React from 'react';

type IconProps = React.SVGProps<SVGSVGElement>;

const baseProps = (extra?: IconProps): IconProps => ({
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true,
  focusable: false,
  ...extra,
});

// TripAdvisor — simplified owl-eyes mark. Two big rings (the eyes)
// with pupils, all packed into the 24-unit viewBox.
export const TripAdvisorIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    {/* Left eye */}
    <path d="M7.5 8.5a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0 1.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z" />
    <circle cx="7.5" cy="12.5" r="1" />
    {/* Right eye */}
    <path d="M16.5 8.5a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0 1.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z" />
    <circle cx="16.5" cy="12.5" r="1" />
    {/* Bridge — small bow between the eyes */}
    <path d="M10.6 7.7c.46-.13.93-.2 1.4-.2.47 0 .94.07 1.4.2l1.05-1.2H9.55l1.05 1.2Z" />
  </svg>
);

// TikTok — stylized quaver / music-note mark, single-color version
// of the official wordmark glyph.
export const TikTokIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.78a8.16 8.16 0 0 0 4.77 1.52V6.86a4.85 4.85 0 0 1-1.84-.17Z" />
  </svg>
);

// Instagram — rounded square frame + lens + LED dot. Simple-icons
// style mark.
export const InstagramIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.71 3.71 0 0 1-.9 1.38c-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.71 3.71 0 0 1-1.38-.9 3.71 3.71 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2Zm0 5.13a4.67 4.67 0 1 0 0 9.34 4.67 4.67 0 0 0 0-9.34Zm0 1.8a2.87 2.87 0 1 1 0 5.74 2.87 2.87 0 0 1 0-5.74Zm4.88-3.27a1.09 1.09 0 1 0 0 2.18 1.09 1.09 0 0 0 0-2.18Z" />
  </svg>
);

// LinkedIn — "in" mark in a rounded square.
export const LinkedinIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.45v6.29ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z" />
  </svg>
);

// YouTube — rounded-rect plate with center play triangle.
export const YoutubeIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    <path d="M23.5 6.5a3 3 0 0 0-2.12-2.12C19.5 4 12 4 12 4s-7.5 0-9.38.38A3 3 0 0 0 .5 6.5C.12 8.38.12 12 .12 12s0 3.62.38 5.5a3 3 0 0 0 2.12 2.12C4.5 20 12 20 12 20s7.5 0 9.38-.38a3 3 0 0 0 2.12-2.12C23.88 15.62 23.88 12 23.88 12s0-3.62-.38-5.5ZM9.75 15.5v-7l6.25 3.5-6.25 3.5Z" />
  </svg>
);

// WhatsApp — chat bubble with handset, simple-icons style.
export const WhatsAppIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    <path d="M20.52 3.48A11.78 11.78 0 0 0 12.05 0C5.48 0 .13 5.36.13 11.94c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.65a11.93 11.93 0 0 0 5.77 1.47h.01c6.57 0 11.92-5.36 11.92-11.94 0-3.19-1.24-6.18-3.45-8.4Zm-8.47 18.36h-.01a9.9 9.9 0 0 1-5.05-1.38l-.36-.22-3.72.98 1-3.63-.24-.37a9.94 9.94 0 0 1-1.52-5.28C2.15 6.45 6.6 2 12.06 2c2.64 0 5.13 1.03 7 2.9a9.86 9.86 0 0 1 2.9 7c0 5.47-4.45 9.94-9.91 9.94Zm5.44-7.43c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48a9 9 0 0 1-1.66-2.07c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37c-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.21 5.08 4.5.71.3 1.27.49 1.7.63.71.23 1.36.2 1.88.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
  </svg>
);

// Telegram — paper-plane mark.
export const TelegramIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    <path d="M9.42 15.18 9.27 19.5c.39 0 .56-.17.77-.37l1.84-1.77 3.82 2.8c.7.4 1.2.19 1.39-.65l2.52-11.83c.22-1.04-.38-1.45-1.07-1.2L3.5 11.07c-1.02.4-1 .97-.18 1.23l3.85 1.2 8.93-5.63c.42-.28.81-.13.5.15l-7.18 7.16Z" />
  </svg>
);

// WeChat — two stacked chat bubbles, simple-icons style.
export const WeChatIcon: React.FC<IconProps> = (props) => (
  <svg {...baseProps(props)}>
    <path d="M8.69 4.5C4.39 4.5.89 7.4.89 11c0 1.95 1.04 3.7 2.71 4.92l-.68 2.04 2.4-1.21c.85.18 1.55.32 2.41.32.21 0 .42 0 .63-.03a5.2 5.2 0 0 1-.2-1.43c0-3.04 2.92-5.52 6.5-5.52.21 0 .42.01.63.03C14.74 6.7 11.97 4.5 8.69 4.5ZM6.16 8.59c.48 0 .96.32.96.96 0 .49-.48.97-.96.97a.97.97 0 0 1-.96-.97c0-.64.48-.96.96-.96Zm5.06 0c.48 0 .96.32.96.96 0 .49-.48.97-.96.97a.97.97 0 0 1-.96-.97c0-.64.48-.96.96-.96Zm3.45 2.71c-3.07 0-5.5 2.13-5.5 4.74 0 2.62 2.43 4.74 5.5 4.74.65 0 1.3-.11 1.95-.32l1.95 1.07-.55-1.72c1.4-1.05 2.16-2.4 2.16-3.77 0-2.61-2.43-4.74-5.5-4.74Zm-1.77 1.96c.39 0 .65.26.65.65 0 .26-.26.65-.65.65a.65.65 0 0 1-.78-.65c0-.39.39-.65.78-.65Zm3.55 0c.39 0 .65.26.65.65 0 .26-.26.65-.65.65a.65.65 0 0 1-.78-.65c0-.39.39-.65.78-.65Z" />
  </svg>
);
